import { POLICY_COMPANY_PLACEHOLDERS, type PlatformCompanySettings } from '@inflip/contracts/admin';
import { useEffect, useState } from 'react';

import { Field, SavedAt, usePlatformSettings } from '../components/settings-form';
import { Button, ErrorBanner, LoadingRow, PageHeader, Section, inputCls } from '../components/ui';
import { api } from '../lib/api-client';

type FieldKey = keyof PlatformCompanySettings;

/** 칸 하나 — 이름·힌트·최대 길이 */
interface FieldSpec {
  key: FieldKey;
  label: string;
  hint?: string;
  placeholder?: string;
  max: number;
}

const BUSINESS_FIELDS: FieldSpec[] = [
  { key: 'businessName', label: '상호', max: 100 },
  { key: 'representative', label: '대표자', max: 50 },
  { key: 'businessNumber', label: '사업자등록번호', placeholder: '000-00-00000', max: 20 },
  {
    key: 'mailOrderNumber',
    label: '통신판매업 신고번호',
    placeholder: '제0000-지역-0000호',
    max: 50,
  },
  { key: 'address', label: '사업장 주소', max: 200 },
  {
    key: 'hostingProvider',
    label: '호스팅 서비스 제공자',
    hint: '전자상거래법상 표시 항목이에요. 예: Amazon Web Services',
    max: 100,
  },
];

const SUPPORT_FIELDS: FieldSpec[] = [
  { key: 'customerEmail', label: '고객센터 이메일', max: 200 },
  { key: 'customerPhone', label: '고객센터 전화', max: 30 },
  {
    key: 'customerHours',
    label: '운영 시간',
    placeholder: '평일 10:00~18:00 (주말·공휴일 휴무)',
    max: 100,
  },
];

const PRIVACY_FIELDS: FieldSpec[] = [
  { key: 'privacyOfficerName', label: '개인정보 보호책임자', hint: '성명 또는 직책', max: 50 },
  { key: 'privacyOfficerContact', label: '보호책임자 연락처', hint: '이메일 또는 전화', max: 200 },
];

/** 자리 표시 이름 → 칸 이름 (안내 표에 쓴다) */
const PLACEHOLDER_BY_FIELD = Object.fromEntries(
  Object.entries(POLICY_COMPANY_PLACEHOLDERS).map(([name, field]) => [field, name]),
) as Partial<Record<FieldKey, string>>;

/**
 * 회사 정보 — 웹 하단 사업자 정보와 약관 본문의 {{상호}} 같은 자리 표시에 쓰인다.
 * 모든 칸은 비워 둘 수 있고, 빈 칸은 하단에서 빠진다
 */
export function CompanySettingsPage() {
  const { session, settings, error, run } = usePlatformSettings();
  const [form, setForm] = useState<PlatformCompanySettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (settings && !form) setForm(settings.company);
  }, [settings, form]);

  if (!session) return null;

  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    setSavedAt(null);
    const ok = await run(() => api.updateCompanySettings(session.accessToken, form));
    if (ok) setSavedAt(new Date());
    setSaving(false);
  };

  const fields = (specs: FieldSpec[]) =>
    form && (
      <div className="grid gap-3 sm:grid-cols-2">
        {specs.map((f) => (
          <Field
            key={f.key}
            label={f.label}
            hint={[
              f.hint,
              PLACEHOLDER_BY_FIELD[f.key] && `약관에서 {{${PLACEHOLDER_BY_FIELD[f.key]}}}`,
            ]
              .filter(Boolean)
              .join(' · ')}
          >
            <input
              className={`w-full ${inputCls}`}
              value={form[f.key]}
              maxLength={f.max}
              placeholder={f.placeholder}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            />
          </Field>
        ))}
      </div>
    );

  return (
    <main className="mx-auto w-full max-w-3xl px-6 py-8">
      <PageHeader
        title="회사 정보"
        description="웹 하단 사업자 정보와 약관 본문에 그대로 쓰여요. 저장하면 바로 반영돼요"
        actions={
          <>
            <SavedAt at={savedAt} />
            <Button disabled={saving || !form} onClick={() => void save()}>
              {saving ? '저장 중…' : '저장'}
            </Button>
          </>
        }
      />
      {error && <ErrorBanner message={error} />}
      {!settings && !error && <LoadingRow />}

      {form && (
        <>
          <Section title="사업자 정보">{fields(BUSINESS_FIELDS)}</Section>
          <Section title="고객센터">{fields(SUPPORT_FIELDS)}</Section>
          <Section title="개인정보 보호책임자">{fields(PRIVACY_FIELDS)}</Section>
          <Section title="약관에 넣기">
            <p className="text-xs text-muted-foreground">
              약관 본문에 <code className="rounded bg-muted px-1">{'{{상호}}'}</code>처럼 쓰면
              사용자에게 보일 때 위 값으로 바뀌어요. 회사 정보를 고치면 약관을 다시 게시하지 않아도
              모든 약관에 반영되고, 비어 있는 값은 “-”로 보여요.
            </p>
          </Section>
        </>
      )}
    </main>
  );
}
